// ─────────────────────────────────────────────────────────────────────────
// Type sheet — every text style at its EXACT desktop value.
// Rendered with inline styles (not Tailwind classes) so a Figma export
// carries the precise size, weight, line height and tracking, with no
// responsive breakpoint shifting the result.
// ─────────────────────────────────────────────────────────────────────────

const SERIF = "'DM Serif Display', Georgia, serif"
const GOTHAM = "'Montserrat', 'Helvetica Neue', Arial, sans-serif"

export type TypeSpec = {
  /** Name to give the Figma text style */
  figma: string
  sample: string
  family: string
  /** Human-readable family name for the spec column */
  familyName: string
  weight: number
  weightName: string
  /** px */
  size: number
  /** px */
  lineHeight: number
  /** em — paste as a percentage in Figma (0.2em = 20%) */
  tracking: number
  uppercase: boolean
  colour: string
  /** Mobile override, where the style scales down */
  mobileSize?: number
}

export const typeSpecs: TypeSpec[] = [
  {
    figma: 'Display/Hero',
    sample: 'A cup worth carrying',
    family: SERIF,
    familyName: 'DM Serif Display',
    weight: 400,
    weightName: 'Regular',
    size: 60,
    lineHeight: 60,
    tracking: -0.03,
    uppercase: false,
    colour: '#3B302F',
    mobileSize: 40,
  },
  {
    figma: 'Display/H1',
    sample: 'Three sizes. One of them is yours.',
    family: SERIF,
    familyName: 'DM Serif Display',
    weight: 400,
    weightName: 'Regular',
    size: 48,
    lineHeight: 48,
    tracking: -0.03,
    uppercase: false,
    colour: '#3B302F',
    mobileSize: 36,
  },
  {
    figma: 'Display/H2',
    sample: 'Ranges drawn from South Africa.',
    family: SERIF,
    familyName: 'DM Serif Display',
    weight: 400,
    weightName: 'Regular',
    size: 36,
    lineHeight: 40,
    tracking: -0.03,
    uppercase: false,
    colour: '#3B302F',
  },
  {
    figma: 'Display/H3',
    sample: 'Custom sleeves',
    family: SERIF,
    familyName: 'DM Serif Display',
    weight: 400,
    weightName: 'Regular',
    size: 24,
    lineHeight: 29,
    tracking: -0.03,
    uppercase: false,
    colour: '#3B302F',
  },
  {
    figma: 'Display/Quote',
    sample: 'The cup you actually want to carry.',
    family: SERIF,
    familyName: 'DM Serif Display Italic',
    weight: 400,
    weightName: 'Italic',
    size: 30,
    lineHeight: 38,
    tracking: -0.03,
    uppercase: false,
    colour: '#432C47',
  },
  {
    figma: 'Eyebrow/Large',
    sample: 'Choose Your Story',
    family: GOTHAM,
    familyName: 'Gotham Bold — sub. Montserrat Bold',
    weight: 700,
    weightName: 'Bold',
    size: 11,
    lineHeight: 15,
    tracking: 0.2,
    uppercase: true,
    colour: '#432C47',
  },
  {
    figma: 'Eyebrow/Small',
    sample: 'Minimum order',
    family: GOTHAM,
    familyName: 'Gotham Bold — sub. Montserrat Bold',
    weight: 700,
    weightName: 'Bold',
    size: 9,
    lineHeight: 13,
    tracking: 0.2,
    uppercase: true,
    colour: '#3B302F',
  },
  {
    figma: 'Body/Large',
    sample:
      'Bamboo fibre cups in three sizes, printed with the work of artists we love.',
    family: GOTHAM,
    familyName: 'Gotham Light — sub. Montserrat Light',
    weight: 300,
    weightName: 'Light',
    size: 18,
    lineHeight: 31,
    tracking: -0.02,
    uppercase: false,
    colour: '#2B2422',
  },
  {
    figma: 'Body/Base',
    sample:
      'The right size depends on what you drink and how long you take over it.',
    family: GOTHAM,
    familyName: 'Gotham Light — sub. Montserrat Light',
    weight: 300,
    weightName: 'Light',
    size: 16,
    lineHeight: 27,
    tracking: -0.02,
    uppercase: false,
    colour: '#2B2422',
  },
  {
    figma: 'Body/Small',
    sample: 'Lids and sleeves are sold separately in every colourway we make.',
    family: GOTHAM,
    familyName: 'Gotham Light — sub. Montserrat Light',
    weight: 300,
    weightName: 'Light',
    size: 14,
    lineHeight: 24,
    tracking: -0.02,
    uppercase: false,
    colour: '#2B2422',
  },
  {
    figma: 'Body/XSmall',
    sample: 'Free nationwide delivery over R 750.',
    family: GOTHAM,
    familyName: 'Gotham Light — sub. Montserrat Light',
    weight: 300,
    weightName: 'Light',
    size: 12,
    lineHeight: 20,
    tracking: -0.02,
    uppercase: false,
    colour: '#2B2422',
  },
  {
    figma: 'CTA/Primary',
    sample: 'Shop now',
    family: GOTHAM,
    familyName: 'Gotham Medium — sub. Montserrat Medium',
    weight: 500,
    weightName: 'Medium',
    size: 11,
    lineHeight: 11,
    tracking: 0.09,
    uppercase: true,
    colour: '#3B302F',
  },
  {
    figma: 'CTA/Small',
    sample: 'Enquire about this',
    family: GOTHAM,
    familyName: 'Gotham Medium — sub. Montserrat Medium',
    weight: 500,
    weightName: 'Medium',
    size: 10,
    lineHeight: 10,
    tracking: 0.09,
    uppercase: true,
    colour: '#432C47',
  },
  {
    figma: 'UI/Label',
    sample: 'Truth Coffee, Cape Town',
    family: GOTHAM,
    familyName: 'Gotham Medium — sub. Montserrat Medium',
    weight: 500,
    weightName: 'Medium',
    size: 16,
    lineHeight: 24,
    tracking: -0.01,
    uppercase: false,
    colour: '#3B302F',
  },
  {
    figma: 'UI/Label Small',
    sample: '350ml · Everyday amount',
    family: GOTHAM,
    familyName: 'Gotham Medium — sub. Montserrat Medium',
    weight: 500,
    weightName: 'Medium',
    size: 13,
    lineHeight: 20,
    tracking: -0.01,
    uppercase: false,
    colour: '#3B302F',
  },
]

/** Full character sets, for checking the fonts loaded correctly in Figma. */
export const charSets = [
  { label: 'Uppercase', chars: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ' },
  { label: 'Lowercase', chars: 'abcdefghijklmnopqrstuvwxyz' },
  { label: 'Numerals', chars: '0123456789' },
  { label: 'Punctuation & symbols', chars: '& @ # % ® · — – “ ” ’ R ml' },
]

export const fontSources = [
  {
    name: 'DM Serif Display',
    role: 'All headlines, story titles and pull-quotes',
    weights: 'Regular 400 · Italic 400',
    where: 'Free — Google Fonts. Install locally, or enable in Figma.',
    licence: 'Open Font License',
  },
  {
    name: 'Gotham',
    role: 'Eyebrows, body copy, buttons and UI labels',
    weights: 'Light 300 · Medium 500 · Bold 700',
    where: 'Licensed — Hoefler&Co. Use only if the client licence covers it.',
    licence: 'Commercial licence required',
  },
  {
    name: 'Montserrat',
    role: 'Web substitute for Gotham, used in this build',
    weights: 'Light 300 · Medium 500 · Bold 700',
    where: 'Free — Google Fonts. Already enabled in Figma by default.',
    licence: 'Open Font License',
  },
]
